import { entities, enums, UserAuthenticationError } from "@fraction/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useLDClient } from "launchdarkly-react-client-sdk";
import React, { useCallback } from "react";
import fraction from "src/api/fraction";
import { setScopeForUser } from "src/api/sentry";
import { useToken } from "src/hooks";
import { idbClear, useCachedQuery } from "src/hooks/useCache";
import { useMutation } from "src/lib";
import { generateAlertMessage } from "src/utilities/interface/generateAlert";

import { loadUser, login, logout, STORAGE } from "./mutations";

const QUERY_KEY = ["authenticate"];

/**
 * Read-only auth state: the current-user query plus the flags derived from it.
 *
 * This intentionally does NOT instantiate the auth mutations, the LaunchDarkly
 * client, or local state — so a component that only needs to know "who is
 * logged in / are we still loading" can subscribe to just this. Pulling the
 * full `useAuth` (mutations + LD) into a render path that only reads user state
 * was a source of widespread instability (e.g. the registration-modal gate
 * mounting/unmounting and re-triggering work in a loop). Prefer `useAuthUser`
 * unless you actually need a mutation (login/logout/register/etc.).
 */
export const useUpdateLocalUser = () => {
  const queryClient = useQueryClient();
  const { token } = useToken();

  // Functional updater avoids depending on `user`, keeping a stable reference
  const updateLocalUser = useCallback(
    (data: Partial<entities.User>) =>
      queryClient.setQueryData([...QUERY_KEY, data?.id], (prev: entities.User | undefined) =>
        prev ? { ...prev, ...data } : undefined
      ),
    [queryClient]
  );

  return {
    token,
    updateLocalUser,
    isAuthenticated: !!token,
  };
};

export const useAuthUser = <TSelected = entities.User | null>({
  refetch: shouldRefetch = false,
  initialRefetch = false,
  select,
}: {
  refetch?: boolean;
  initialRefetch?: boolean;
  /**
   * Narrow the cached user down to a slice. Because this maps to react-query's
   * `select`, the component only re-renders when the selected slice changes —
   * e.g. `select: (u) => u?.email` won't re-render on unrelated user updates.
   * When provided, the full-user conveniences (`userId`, `isAccountRegistered`)
   * are `undefined`/derived from token only; read what you need from `user`.
   */
  select?: (user: entities.User | null) => TSelected;
} = {}) => {
  const { token, id, email, clearToken } = useToken();

  const { data, error, isLoading, isError, isFetching, dataUpdatedAt, refetch } = useCachedQuery<
    entities.User | null,
    any,
    TSelected
  >({
    initialRefetch: initialRefetch,
    refetchInterval: shouldRefetch ? undefined : false,
    refetchOnMount: shouldRefetch,
    refetchOnReconnect: shouldRefetch,
    refetchOnWindowFocus: shouldRefetch,
    enabled: !!token,
    queryKey: [...QUERY_KEY, token],
    select,
    placeholderData: () => {
      const storedUser = STORAGE.getUser();
      if (storedUser) {
        return storedUser;
      }
      return id ? new entities.User({ id, email }) : undefined;
    },
    queryFn: async () => {
      if (!token) {
        return null;
      }
      try {
        return await loadUser(token);
      } catch (err) {
        if (err instanceof UserAuthenticationError) {
          clearToken();
        }
        throw err;
      }
    },
    onSuccess: (loaded) => {
      if (loaded) {
        STORAGE.setUser(loaded);
      }
    },
  });

  // Without a select, `data` is the full user so the user-derived conveniences
  // are meaningful. With a select the consumer has opted into a slice and reads
  // it from `user`; the conveniences below fall back to token-only.
  const fullUser = (select ? undefined : data) as entities.User | null | undefined;

  return {
    token,
    user: data,
    userId: fullUser?.id,
    error: error as Error,
    refetchUser: refetch,
    isLoadingUser: isLoading || (!dataUpdatedAt && isFetching),
    isLoadingUserError: isError,
    isAuthenticated: !!token || !!fullUser?.accountStatus,
    isAccountRegistered: fullUser?.accountStatus === enums.UserAccountStatus.REGISTERED,
  };
};

/**
 * The shared "a user just authenticated / changed" side effect: identify them
 * in LaunchDarkly, set the Sentry scope, and write them into the auth-query
 * cache. Only needs the query client + LD client — notably NOT the user query
 * or the token store — so the per-mutation hooks below stay lightweight.
 */
export const useSetUser = () => {
  const queryClient = useQueryClient();
  const ldClient = useLDClient();

  return useCallback((data: entities.User) => {
    if (ldClient?.identify) {
      ldClient.identify({ key: data?.id, ...(data || {}) });
    }
    setScopeForUser(data);
    return queryClient.setQueriesData({ queryKey: QUERY_KEY }, data);
  }, []);
};

/**
 * Update the current user's fields. Returns the mutation plus `updatingUserFields`
 * (the in-flight optimistic patch) so callers can show pending state per-field.
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const [updatingUserFields, setUpdatingUserFields] = React.useState<Partial<entities.UserT>>({});

  const updateLocalUser = useCallback(
    (data: Partial<entities.User>) =>
      queryClient.setQueryData([...QUERY_KEY, data?.id], (prev: entities.User | undefined) =>
        prev ? { ...prev, ...data } : undefined
      ),
    [queryClient]
  );

  const updateUser = useMutation({
    mutationFn: async (data: Partial<entities.User>) => {
      setUpdatingUserFields(data);
      updateLocalUser(data);
      try {
        const updated = await fraction.updateUser(data);
        updateLocalUser(updated);
      } finally {
        setUpdatingUserFields({});
      }
    },
  });

  return { updateUser, updatingUserFields };
};

export const useLogin = () => {
  const setUser = useSetUser();
  const { setToken } = useToken();

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: (userData) => {
      // No token means the account needs verification — the caller routes into
      // the claim/verify flow; don't set a session here.
      if (userData?.token) {
        setUser(userData.user);
        setToken(userData.token);
      }
    },
    meta: {
      errorMessage: (err: any) =>
        generateAlertMessage(err, "There was an error logging in. Please try again."),
    },
  });

  return { login: loginMutation.mutateAsync, isLoggingIn: loginMutation.isPending };
};

export const useResetPassword = () => {
  const queryClient = useQueryClient();
  // const setUser = useSetUser();

  const resetPasswordMutation = useMutation({
    mutationFn: (password: string) => {
      return fraction.resetUserPassword(password);
    },
    onSuccess: (userData) => {
      // setUser(userData);
      queryClient.refetchQueries({ queryKey: QUERY_KEY });
    },
    meta: { errorMessage: "There was an error resetting your password. Please try again." },
  });

  return {
    resetPassword: resetPasswordMutation.mutateAsync,
    isResettingPassword: resetPasswordMutation.isPending,
  };
};

/**
 * Register a password for the current (passwordless) user. Deliberately minimal
 * — only the register mutation + `setUser` — so the registration-modal flow can
 * instantiate it without pulling in the token store or the other mutations.
 */
export const useRegisterPassword = ({
  onSuccessfulRegistration,
}: {
  onSuccessfulRegistration?: (user: entities.User) => void;
} = {}) => {
  const setUser = useSetUser();

  const registerPasswordMutation = useMutation({
    mutationFn: (password: string) => fraction.registerUserPassword(password),
    onSuccess: (userData) => {
      setUser(userData);
      onSuccessfulRegistration?.(userData);
    },
    meta: { errorMessage: "There was an error registering. Please try again." },
  });

  return {
    registerPassword: registerPasswordMutation.mutateAsync,
    isRegisteringPassword: registerPasswordMutation.isPending,
  };
};

export const useSignUp = () => {
  const setUser = useSetUser();
  const { setToken } = useToken();

  const signUpMutation = useMutation({
    mutationFn: async (credentials: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      brokerageId?: string;
      createBrokerAccount?: boolean;
    }) => {
      const newToken = await fraction.createUserWithPassword(credentials);
      const fetched = await fraction.getUser(newToken);
      return { user: fetched, token: newToken };
    },
    onSuccess: ({ user: created, token: newToken }) => {
      setUser(created);
      setToken(newToken);
    },
    meta: {
      errorMessage: (err: any) =>
        generateAlertMessage(err, "There was an error creating your account. Please try again."),
    },
  });

  return {
    signUp: signUpMutation.mutateAsync,
    isSigningUp: signUpMutation.isPending,
    signUpError: signUpMutation.error as Error | null,
    signUpResult: signUpMutation.data,
  };
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  const { clearToken } = useToken();

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      clearToken();
      STORAGE.clearUser();
      STORAGE.clearToken();
      queryClient.removeQueries();
      idbClear();
    },
    meta: { errorMessage: "There was an error logging out. Please try again." },
  });

  return { logout: logoutMutation.mutateAsync, isLoggingOut: logoutMutation.isPending };
};
